import { NetworkStatus } from "@apollo/client";
import { Error, UpToTop } from "@carbon/icons-react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import ScrollToTop from "react-scroll-to-top";

import PageLoader from "../../components/PageLoader";
import DeliveryList from "../../components/deliveryList/DeliveryList";
import FallbackComponent from "../../components/fallbackComponent/FallbackComponent";
import { deliveriesListTags } from "../../config/tagList";
import { useHeader } from "../../contexts/PageHeaderContext";
import useInboundDeliveriesQuery from "../../hooks/graphql-hooks/queries/useInboundDeliveriesQuery";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import {
  InboundDeliveriesQueryVariables,
  InboundDeliveryOverviewPagedFragment,
  SortInput,
} from "../../models/autogenerated-deliveryService-gql/graphql";
import { ITag } from "../../models/tag";
import "./DeliveriesOverview.scss";
import TagFilter from "./components/tagFilter/TagFilter";

const pageSize = 6;

const initialQueryParameters: InboundDeliveriesQueryVariables = {
  offset: 0,
  limit: pageSize,
  sort: SortInput.Desc,
};

const DeliveriesOverview = () => {
  //#region State and Variables
  const { t } = useTranslation();
  const { updateHeader, updateSecondHeader } = useHeader();
  const [scrolledToBottom, resetScrolledToBottom] = useInfiniteScroll();
  const [searchParams, setSearchParams] = useSearchParams();

  const [inboundDeliveriesPaged, setInboundDeliveriesPaged] =
    useState<InboundDeliveryOverviewPagedFragment>();

  const inboundDeliveries = inboundDeliveriesPaged?.data ?? [];
  const tagNameFromUrl = searchParams.get("tag");
  const selectedTag =
    deliveriesListTags.find((tag) => tag.name === tagNameFromUrl) || null;
  const queryParameters = {
    ...initialQueryParameters,
    ...selectedTag?.inputParameters,
  };
  //#endregion

  //#region Graphql initialization
  const [inboundDeliveriesQuery, { loading, error, fetchMore, networkStatus }] =
    useInboundDeliveriesQuery({
      dataAction: (collection) => {
        setInboundDeliveriesPaged(collection);
      },
    });
  //#endregion

  //#region Component functions
  const getInboundDeliveries = () => {
    inboundDeliveriesQuery({ variables: queryParameters }).catch(() => {});
  };

  const fetchMoreDeliveries = () => {
    if (!inboundDeliveriesPaged?.pageInfo.hasNextPage) {
      resetScrolledToBottom();
      return;
    }

    fetchMore({
      variables: {
        offset: inboundDeliveries.length,
      },
      updateQuery: (prev, { fetchMoreResult }) => fetchMoreResult,
    })
      .catch(() => {})
      .finally(() => resetScrolledToBottom());
  };

  const selectTag = (tag: ITag) => {
    const newTag = tag.name === selectedTag?.name ? null : tag;
    setSearchParams(newTag ? { tag: newTag.name } : {});
  };

  useEffect(() => {
    getInboundDeliveries();
  }, [searchParams]);
  //#endregion

  //#region Lifecycle functions
  useEffect(() => {
    updateHeader(<>{t("yourDeliveries")}</>);
    updateSecondHeader(null);

    getInboundDeliveries();
  }, []);

  useEffect(() => {
    if (scrolledToBottom) fetchMoreDeliveries();
  }, [scrolledToBottom]);
  //#endregion

  //#region Render functions
  const deliveryList =
    networkStatus === NetworkStatus.loading ||
    (loading && networkStatus !== NetworkStatus.fetchMore) ? (
      <PageLoader />
    ) : (
      <DeliveryList items={inboundDeliveries} />
    );

  if (error)
    return (
      <FallbackComponent
        middleIcon={<Error size={32} />}
        text={t("deliveriesError")}
      />
    );

  return (
    <div className="page-content deliveries-overview">
      <Helmet title={t("deliveries")} />
      <TagFilter
        selectTag={selectTag}
        selectedTag={selectedTag}
        tagList={deliveriesListTags}
      />
      {deliveryList}
      {networkStatus === NetworkStatus.fetchMore && (
        <PageLoader className="mt-15" text={t("loadingMoreEntries")} />
      )}
      <ScrollToTop style={{ bottom: "80px" }} smooth component={<UpToTop />} />
    </div>
  );
};
//#endregion
export default DeliveriesOverview;
